// components/Navigation.tsx - FennoFuture navigation
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, Home, Wrench, Phone
} from 'lucide-react';

const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Etusivu', icon: <Home className="w-4 h-4" /> },
    { path: '/palvelut', label: 'Palvelut', icon: <Wrench className="w-4 h-4" /> },
    { path: '/yhteystiedot', label: 'Yhteystiedot', icon: <Phone className="w-4 h-4" /> },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-lg py-3'
          : location.pathname === '/'
            ? 'bg-gradient-to-b from-gray-900/50 to-transparent backdrop-blur-sm py-5'
            : 'bg-white shadow-md py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative w-11 h-11 transform transition-all duration-300 group-hover:scale-110">
                <img src="/logo.png" alt="FennoFuture" className="w-full h-full object-contain" />
              </div>
              <div>
                <span className={`text-xl font-bold transition-colors duration-300 ${
                  isScrolled || location.pathname !== '/' ? 'text-gray-900' : 'text-white'
                }`}>
                  FennoFuture
                </span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 font-medium transition-all duration-300 rounded-lg group flex items-center gap-2 ${
                    isActive(item.path)
                      ? isScrolled || location.pathname !== '/'
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-white bg-white/10'
                      : isScrolled || location.pathname !== '/'
                        ? 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                        : 'text-gray-200 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span className="hidden xl:block">{item.icon}</span>
                  {item.label}
                  {isActive(item.path) && (
                    <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/4 h-0.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full" />
                  )}
                </Link>
              ))}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden relative p-2.5 rounded-lg transition-all duration-300 ${
                isScrolled || location.pathname !== '/'
                  ? 'text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-6">
                <span className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-180 opacity-0' : 'rotate-0 opacity-100'
                }`}>
                  <Menu className="w-6 h-6" />
                </span>
                <span className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-0 opacity-100' : '-rotate-180 opacity-0'
                }`}>
                  <X className="w-6 h-6" />
                </span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
        isMobileMenuOpen ? 'visible' : 'invisible'
      }`}>
        <div
          className={`absolute inset-0 bg-gray-900 transition-opacity duration-500 ${
            isMobileMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        <div className={`absolute right-0 top-0 h-full w-full sm:w-80 bg-white shadow-2xl transition-transform duration-500 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full pt-20 px-6 pb-6">
            <nav className="flex-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 py-3 text-lg font-medium transition-all duration-300 ${
                    isActive(item.path)
                      ? 'text-blue-600'
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                >
                  {item.icon}
                  {item.label}
                  {isActive(item.path) && (
                    <span className="ml-auto w-2 h-2 bg-blue-600 rounded-full" />
                  )}
                </Link>
              ))}
            </nav>

            <a
              href="tel:+358400416466"
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full font-medium hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Soita meille
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
